import React, { useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import CSVColumnSelector from './CSVColumnSelector';
import Papa from 'papaparse';
import uploadIcon from '../../assets/icons/upload_icon.svg';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const FileUpload = forwardRef(({
  onFileSelect, 
  className = '', 
  onSourceLanguageChange, 
  onTargetLanguageChange,
  setIsSourceAutoDetected,
  setIsTargetAutoDetected,
  isMetricsMode = false,
  summaryMeta = null
}, ref) => {
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [csvData, setCsvData] = useState(null);
  const [fileName, setFileName] = useState('');
  const [selectedColumns, setSelectedColumns] = useState(null);
  const [fileSize, setFileSize] = useState('');
  const [error, setError] = useState('');
  const [noHeaders, setNoHeaders] = useState(false);
  const [originalFile, setOriginalFile] = useState(null);
  const [modalKey, setModalKey] = useState(0);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const onDrop = useCallback((acceptedFiles) => {
    setError('');
    const file = acceptedFiles[0];
    
    if (file.size > MAX_FILE_SIZE) {
      setError(`<b>The file is too large</b>. Please reduce the file size below 10MB and try again`);
      return;
    }

    if (file) {
      setFileName(file.name);
      setFileSize(formatFileSize(file.size));
      setOriginalFile(file);
      setModalKey(prev => prev + 1);
      Papa.parse(file, {
        complete: (results) => {
          if (results.data && results.data.length > 0) {
            let headers, data;
            if (noHeaders) {
              headers = results.data[0];
              data = results.data;
            } else {
              headers = results.data[0];
              data = results.data.slice(1);
            }
            setCsvData({
              headers,
              data
            });
            setShowColumnSelector(true);
          }
        },
        header: false,
        skipEmptyLines: true,
        escapeChar: "\\",
        newline: "\n",
      });
    }
  }, [noHeaders]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv']
    },
    maxFiles: 1,
    noClick: true,
    noKeyboard: true
  });

  useImperativeHandle(ref, () => ({
    open
  }));

  const handleColumnSelect = (selection) => {
    setSelectedColumns(selection);
    const { sourceColumn, targetColumn, referenceColumn } = selection;
    
    const getOriginalColumnName = (columnName) => {
      return columnName.replace(/\s*\(\d+\)$/, '');
    };
    
    const sourceIndex = csvData.headers.findIndex(h => getOriginalColumnName(h) === sourceColumn);
    const targetIndex = csvData.headers.findIndex(h => getOriginalColumnName(h) === targetColumn);
    const referenceIndex = referenceColumn ? csvData.headers.findIndex(h => getOriginalColumnName(h) === referenceColumn) : -1;
  
    const processedData = csvData.data.map(row => {
      // Create an object with all columns from the CSV
      const rowData = {};
      csvData.headers.forEach((header, index) => {
        const originalHeader = getOriginalColumnName(header);
        rowData[originalHeader] = row[index];
      });
      
      const result = {
        ...rowData,
        source: row[sourceIndex],
        target: row[targetIndex]
      };
      
      if (isMetricsMode && referenceIndex !== -1) {
        result.reference = row[referenceIndex];
      }
      
      return result;
    });

    onFileSelect(processedData, { columns: csvData.headers.length });
  };

  const handleRemove = () => {
    setShowColumnSelector(false);
    setSelectedColumns(null);
    setFileName('');
    setFileSize('');
    setError('');
    setModalKey(prev => prev + 1);
    onFileSelect([], null);
    onSourceLanguageChange('');
    onTargetLanguageChange('');
    setIsTargetAutoDetected(false);
    setIsSourceAutoDetected(false);
  };

  const handleNoHeadersChange = (checked) => {
    setNoHeaders(checked);
    if (fileName && originalFile) {
      Papa.parse(originalFile, {
        complete: (results) => {
          if (results.data && results.data.length > 0) {
            let headers, data;
            if (checked) {
              headers = results.data[0];
              data = results.data;
            } else {
              headers = results.data[0];
              data = results.data.slice(1);
            }
            setCsvData({
              headers,
              data
            });
          }
        },
        header: false
      });
    }
  };

  return (
    <div className={className}>
      {!selectedColumns ? (
        <div
          {...getRootProps({ onClick: open })}
          className={`cursor-pointer border-2 border-dashed border-[#E2E8F0] rounded-[12px] bg-[#F9FAFB] p-0 min-h-[160px] flex flex-col items-center justify-center transition-colors
            ${isDragActive ? 'border-blue-500 bg-blue-50' : 'hover:border-[#CBD5E1]'}`}
          style={{ width: '100%' }}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center justify-center w-full py-8">
            <img src={uploadIcon} alt="Upload" className="w-10 h-10 mb-4" />
            <p className="text-base text-[#6B7280] mb-4 text-center">Drag and drop a CSV file, or click to select</p>
            <button
              type="button"
              className="bg-white hover:bg-[#F1F5F9] text-[#020817] font-semibold rounded-md px-6 py-2 text-base mb-4 border border-[#E2E8F0] shadow-[0px_1px_2px_0px_#0000000D] focus:outline-none focus:ring-0"
              onClick={e => { e.preventDefault(); e.stopPropagation(); open(); }}
            >
              Browse Files
            </button>
            <p className="text-sm text-[#9CA3AF]">Only CSV files are supported</p>
            {error && (
              <div className="mt-2 text-sm text-red-500" dangerouslySetInnerHTML={{ __html: error }} />
            )}
          </div>
        </div>
      ) : (
        <>
          {summaryMeta && (
            <div className="rounded-lg border border-[#E2E8F0] bg-white px-3 py-2">
              <div className="text-xs text-[#475569]">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-[#0F172A]">Upload summary</div>
                  <button
                    onClick={handleRemove}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-center justify-between rounded-md bg-[#F8FAFC] px-3 py-2">
                      <span>Name</span>
                      <span className="font-semibold text-[#0F172A]">{fileName}</span>
                    </div>
                    <div className="flex items-center justify-between rounded-md bg-[#F8FAFC] px-3 py-2">
                      <span>Size</span>
                      <span className="font-semibold text-[#0F172A]">{fileSize}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between rounded-md bg-[#F8FAFC] px-3 py-2">
                    <span>Rows</span>
                    <span className="font-semibold text-[#0F172A]">{summaryMeta.rows}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-center justify-between rounded-md bg-[#F8FAFC] px-3 py-2">
                      <span>Source column</span>
                      <span className="font-semibold text-[#0F172A]">{selectedColumns.sourceColumn}</span>
                    </div>
                    <div className="flex items-center justify-between rounded-md bg-[#F8FAFC] px-3 py-2">
                      <span>Source chars</span>
                      <span className="font-semibold text-[#0F172A]">{summaryMeta.sourceChars}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-center justify-between rounded-md bg-[#F8FAFC] px-3 py-2">
                      <span>Target column</span>
                      <span className="font-semibold text-[#0F172A]">{selectedColumns.targetColumn}</span>
                    </div>
                    <div className="flex items-center justify-between rounded-md bg-[#F8FAFC] px-3 py-2">
                      <span>Target chars</span>
                      <span className="font-semibold text-[#0F172A]">{summaryMeta.targetChars}</span>
                    </div>
                  </div>
                  {isMetricsMode && selectedColumns.referenceColumn && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="flex items-center justify-between rounded-md bg-[#F8FAFC] px-3 py-2">
                        <span>Reference column</span>
                        <span className="font-semibold text-[#0F172A]">{selectedColumns.referenceColumn}</span>
                      </div>
                      <div className="flex items-center justify-between rounded-md bg-[#F8FAFC] px-3 py-2">
                        <span>Reference chars</span>
                        <span className="font-semibold text-[#0F172A]">{summaryMeta.referenceChars}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      <CSVColumnSelector
        key={modalKey}
        isOpen={showColumnSelector}
        onClose={() => setShowColumnSelector(false)}
        onSave={handleColumnSelect}
        columns={csvData?.headers || []}
        fileName={fileName}
        onSourceLanguageChange={onSourceLanguageChange}
        onTargetLanguageChange={onTargetLanguageChange}
        onNoHeadersChange={handleNoHeadersChange}
        setIsSourceAutoDetected={setIsSourceAutoDetected}
        setIsTargetAutoDetected={setIsTargetAutoDetected}
        isMetricsMode={isMetricsMode}
      />
    </div>
  );
});

export default FileUpload; 